/**
 * Protected Route Wrapper - T039
 *
 * Wrapper component that redirects to login if not authenticated
 */
import { type ReactNode } from 'react';
import { Navigate, useLocation } from '@tanstack/react-router';
import { useAuth } from '../hooks/useAuth';
import { useSession } from '../hooks/useTenantContext';
import LoadingSpinner from './ui/LoadingSpinner';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const session = useSession();
  const { pathname } = useLocation();

  const sessionAuthenticated = !!session.data?.user;
  const authenticated = isAuthenticated || sessionAuthenticated;

  if (isLoading || (!authenticated && session.isLoading)) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/login" />;
  }

  const settingsException =
    pathname === '/settings/animation' || pathname.startsWith('/settings/categories');
  const legacyCrmRoute = [
    '/customers',
    '/invoices',
    '/estimates',
    '/contracts',
    '/reminders',
    '/dashboard',
    '/export',
    '/settings',
  ].some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

  // Wait until role data is known before rendering a protected page. This
  // prevents a brief flash of CRM data while the session query is catching up
  // with the already-resolved auth/me request.
  if (legacyCrmRoute && !settingsException && session.isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (
    legacyCrmRoute &&
    !settingsException &&
    !session.isLoading &&
    session.data?.activeRole === 'steuerberater'
  ) {
    return <Navigate to="/expenses" />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
